import { path } from 'ramda';
import React from 'react';
import styled, { withTheme } from 'styled-components';

import PoiStatusPill, { PoiOpenStatusText } from '@ui/labels/PoiStatusPill.tsx';
import type { PoiOpenStatusResult } from '@ui/SearchResults/types.ts';

import { ParkingLabelStatus } from '../../../../../utils/constants.ts';

const PoiStatusPillStyled = styled(PoiStatusPill)`
  margin-block: 2px 0;
  margin-inline: 52px 0;
`;

const PoiOpenStatusTextStyled = styled(PoiOpenStatusText)`
  margin-block: 2px 0;
  margin-inline: 52px 0;
`;

const OccupancyReport = styled.span`
  margin-block: 2px 0;
  margin-inline: 52px 0;
`;

interface OpenClosedStatusProps {
  poi: {
    dynamicData?: {
      parking?: { lotStatus?: string };
      occupancy?: { occupancy?: number; expiration?: number };
      'open-closed-status'?: { isOpen?: boolean };
    };
  };
  T: (key: string) => string;
  /**
   * Open/closed state resolved on demand by poiDataManager via the poi/getOpenStatus
   * bus message. Undefined when the status is unknown or the feature flag is off.
   */
  openStatus?: PoiOpenStatusResult;
}

/**
 * Displays POI open/closed status in the POI detail view.
 *
 * For parking lots: the pill style with parking-specific statuses.
 * For other POIs: plain colored text driven by the status resolved on demand by
 * poiDataManager. Nothing is read off the POI object, because open status depends on
 * the current time.
 *
 * While the feature flag is off, `openStatus` is undefined and the pre-existing
 * dynamic-data pill continues to render unchanged.
 */
const OpenClosedStatus = withTheme(({ poi, T, openStatus }: OpenClosedStatusProps) => {
  const occupancyOb = path(['dynamicData', 'occupancy'], poi);

  const parkingDD = path(['dynamicData', 'parking'], poi);
  const openCloseDD = path(['dynamicData', 'open-closed-status'], poi);

  let poiStatus;

  if (parkingDD) {
    if (typeof parkingDD.lotStatus !== 'undefined' && parkingDD.lotStatus !== 'Unknown') {
      poiStatus = parkingDD.lotStatus;
    }
  } else if (openCloseDD && !openStatus) {
    // Legacy fallback: while the new open-status feature is off, keep rendering the existing
    // dynamic-data pill so venues relying on it do not silently lose the indicator.
    poiStatus = openCloseDD.isOpen ? ParkingLabelStatus.OPEN : ParkingLabelStatus.CLOSED;
  }

  return (
    <div>
      {poiStatus && <PoiStatusPillStyled status={poiStatus} T={T} />}
      {openStatus && !parkingDD && (
        <PoiOpenStatusTextStyled status={openStatus.status} timeLabel={openStatus.timeLabel} T={T} />
      )}
      {occupancyOb && occupancyOb.expiration > Date.now() ? (
        <OccupancyReport>{occupancyOb.occupancy} Occupants</OccupancyReport>
      ) : null}
    </div>
  );
});

export default OpenClosedStatus;
